import {
  createShopApiGraphqlClient,
  type ShopApiGraphqlClient,
} from "../../../packages/pvc-product/shop-api-transport";

export type CustomerKind = "private" | "business";

export type AccountCustomer = {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber: string | null;
  customerType: CustomerKind;
  businessName: string | null;
  businessId: string | null;
  orderCount: number;
  orders: AccountOrderSummary[];
};

export type AccountOrderSummary = {
  id: string;
  code: string;
  state: string;
  createdAt: string;
  totalQuantity: number;
  totalWithTax: number;
  currencyCode: string;
};

export type AccountOrderPageResult = {
  totalItems: number;
  items: AccountOrderSummary[];
};

export type AccountOrderLineStatus = {
  statusGroup: string;
  statusLabelHe: string;
};

export type AccountOrderLineDetails = {
  kind: "PVC" | "CATALOG" | "OTHER";
  productSlug: string | null;
  widthCm: number | null;
  heightCm: number | null;
  thickness: string | null;
  printSide: string | null;
  shape: string | null;
  finishing: string | null;
  designService: string | null;
  rush: boolean | null;
  addonCodes: string[];
  notes: string | null;
  requestedDate: string | null;
  surchargeWithTax: number;
  totalWithTax: number;
};

export type AccountProofStatus = {
  version: number;
  status: "draft" | "sent" | "changes_requested" | "approved";
  staffNote: string | null;
  customerComment: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  approvedAt: string | null;
};

export type AccountOrderArtworkFile = {
  originalFilename: string;
  version: number;
  downloadUrl: string;
};

export type AccountOrderLine = {
  id: string;
  quantity: number;
  totalWithTax: number;
  productVariantId: string;
  productName: string;
  productionStatus: AccountOrderLineStatus | null;
  details: AccountOrderLineDetails | null;
  proofStatus: AccountProofStatus | null;
  artworkFile: AccountOrderArtworkFile | null;
};

export type AccountBillingSnapshot = {
  customerType: CustomerKind;
  businessName: string | null;
  businessId: string | null;
  billingName: string | null;
  billingEmail: string | null;
  company: string | null;
  streetLine1: string | null;
  streetLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string | null;
  phoneNumber: string | null;
  capturedAt: string | null;
};

export type AccountOrderAddress = {
  fullName: string | null;
  company: string | null;
  streetLine1: string | null;
  streetLine2: string | null;
  city: string | null;
  province: string | null;
  postalCode: string | null;
  countryCode: string | null;
  phoneNumber: string | null;
};

export type AccountShippingMethod = {
  code: string;
  name: string;
  priceWithTax: number;
};

export type AccountOrderDetails = AccountOrderSummary & {
  lines: AccountOrderLine[];
  billingSnapshot: AccountBillingSnapshot | null;
  shippingAddress: AccountOrderAddress | null;
  shippingWithTax: number;
  shippingMethods: AccountShippingMethod[];
};

export type RepeatOrderSuccess = {
  ok: true;
  cartId: string;
  copiedLines: number;
  totalQuantity: number;
  totalWithTax: number;
  currencyCode: string;
  artworkMustBeResupplied: boolean;
};

export type RepeatOrderFailure = {
  ok: false;
  code: string;
  message: string;
};

export type RepeatOrderResult = RepeatOrderSuccess | RepeatOrderFailure;

export type RegisterAccountInput = {
  customerType: CustomerKind;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  password: string;
  businessName?: string;
  businessId?: string;
};

export class AccountDomainError extends Error {
  constructor(public readonly errorCode: string, message: string) {
    super(message);
    this.name = "AccountDomainError";
  }
}

function normalizeIsraeliPhoneForRegistration(value: string): string | null {
  const raw = value.trim();
  if (!raw || !/^[+()0-9\s-]+$/.test(raw)) return null;

  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("00972")) {
    digits = `0${digits.slice(5)}`;
  } else if (digits.startsWith("972")) {
    digits = `0${digits.slice(3)}`;
  }

  if (/^05\d{8}$/.test(digits)) return digits;
  if (/^07\d{8}$/.test(digits)) return digits;
  if (/^0[23489]\d{7}$/.test(digits)) return digits;
  return null;
}

function normalizeIsraeliBusinessIdForRegistration(value: string): string | null {
  const raw = value.trim();
  if (!raw || !/^[0-9\s-]+$/.test(raw)) return null;

  const rawDigits = raw.replace(/\D/g, "");
  if (rawDigits.length < 5 || rawDigits.length > 9) return null;

  const digits = rawDigits.padStart(9, "0");
  if (/^0{9}$/.test(digits)) return null;

  // Israeli company / public-company identifiers are 9 digits beginning with 5.
  if (/^5\d{8}$/.test(digits)) return digits;

  let sum = 0;
  for (let index = 0; index < digits.length; index += 1) {
    let product = Number(digits[index]) * (index % 2 === 0 ? 1 : 2);
    if (product > 9) product -= 9;
    sum += product;
  }
  return sum % 10 === 0 ? digits : null;
}

const ORDER_SUMMARY_FIELDS = `
  id
  code
  state
  createdAt
  totalQuantity
  totalWithTax
  currencyCode
`;

const CUSTOMER_FIELDS = `
  id
  firstName
  lastName
  emailAddress
  phoneNumber
  customFields {
    customerType
    businessName
    businessId
  }
  orders(options: { sort: { createdAt: DESC }, take: 20 }) {
    totalItems
    items { ${ORDER_SUMMARY_FIELDS} }
  }
`;

const ACTIVE_CUSTOMER_QUERY = `
  query HappyPrintAccountCustomer {
    activeCustomer { ${CUSTOMER_FIELDS} }
  }
`;

const ACTIVE_ORDERS_QUERY = `
  query HappyPrintAccountOrders($skip: Int!, $take: Int!) {
    activeCustomer {
      orders(options: { sort: { createdAt: DESC }, skip: $skip, take: $take }) {
        totalItems
        items { ${ORDER_SUMMARY_FIELDS} }
      }
    }
  }
`;

const LOGIN_MUTATION = `
  mutation HappyPrintAccountLogin($emailAddress: String!, $password: String!, $rememberMe: Boolean!) {
    login(username: $emailAddress, password: $password, rememberMe: $rememberMe) {
      __typename
      ... on CurrentUser { id identifier }
      ... on ErrorResult { errorCode message }
    }
  }
`;

const LOGOUT_MUTATION = `
  mutation HappyPrintAccountLogout {
    logout { success }
  }
`;

const REGISTER_MUTATION = `
  mutation HappyPrintRegisterAccount($input: RegisterCustomerInput!) {
    registerCustomerAccount(input: $input) {
      __typename
      ... on Success { success }
      ... on ErrorResult { errorCode message }
    }
  }
`;

const VERIFY_MUTATION = `
  mutation HappyPrintVerifyAccount($token: String!) {
    verifyCustomerAccount(token: $token) {
      __typename
      ... on CurrentUser { id identifier }
      ... on ErrorResult { errorCode message }
    }
  }
`;

const ORDER_QUERY = `
  query HappyPrintAccountOrder($id: ID!) {
    order(id: $id) {
      ${ORDER_SUMMARY_FIELDS}
      shippingWithTax
      shippingAddress {
        fullName
        company
        streetLine1
        streetLine2
        city
        province
        postalCode
        countryCode
        phoneNumber
      }
      shippingLines {
        priceWithTax
        shippingMethod {
          code
          name
        }
      }
      customFields {
        billingSnapshotCustomerType
        billingSnapshotBusinessName
        billingSnapshotBusinessId
        billingSnapshotName
        billingSnapshotEmail
        billingSnapshotCompany
        billingSnapshotStreetLine1
        billingSnapshotStreetLine2
        billingSnapshotCity
        billingSnapshotProvince
        billingSnapshotPostalCode
        billingSnapshotCountryCode
        billingSnapshotPhoneNumber
        billingSnapshotAt
      }
      lines {
        id
        quantity
        totalWithTax
        productVariant { id name }
      }
    }
    myHappyPrintOrderStatus(orderId: $id) {
      orderLineId
      statusGroup
      statusLabelHe
    }
    myHappyPrintArtworkFiles(orderId: $id) {
      orderLineId
      originalFilename
      version
      downloadUrl
    }
    myHappyPrintProofStatuses(orderId: $id) {
      orderLineId
      version
      status
      staffNote
      customerComment
      sentAt
      respondedAt
      approvedAt
    }
    myHappyPrintOrderLineDetails(orderId: $id) {
      orderLineId
      kind
      productSlug
      widthCm
      heightCm
      thickness
      printSide
      shape
      finishing
      designService
      rush
      addonCodes
      notes
      requestedDate
      surchargeWithTax
      totalWithTax
    }
  }
`;

const REPEAT_ORDER_MUTATION = `
  mutation HappyPrintRepeatOrder($orderId: ID!) {
    repeatHappyPrintOrder(orderId: $orderId) {
      __typename
      ... on HappyPrintRepeatOrderSuccess {
        ok
        cartId
        copiedLines
        totalQuantity
        totalWithTax
        currencyCode
        artworkMustBeResupplied
      }
      ... on HappyPrintRepeatOrderFailure {
        ok
        code
        message
      }
    }
  }
`;

function record(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function str(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.length) throw new Error(`${path} must be a non-empty string`);
  return value;
}

function nullableStr(value: unknown, path: string): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new Error(`${path} must be a string or null`);
  return value || null;
}

function int(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isInteger(value)) throw new Error(`${path} must be an integer`);
  return value;
}

function nullableNumber(value: unknown, path: string): number | null {
  if (value == null) return null;
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be a finite number or null`);
  return value;
}

function nullableBool(value: unknown, path: string): boolean | null {
  if (value == null) return null;
  if (typeof value !== "boolean") throw new Error(`${path} must be a boolean or null`);
  return value;
}

function bool(value: unknown, path: string): boolean {
  if (typeof value !== "boolean") throw new Error(`${path} must be a boolean`);
  return value;
}

function decodeOrderSummary(value: unknown, path: string): AccountOrderSummary {
  const order = record(value, path);
  return {
    id: str(order.id, `${path}.id`),
    code: str(order.code, `${path}.code`),
    state: str(order.state, `${path}.state`),
    createdAt: str(order.createdAt, `${path}.createdAt`),
    totalQuantity: int(order.totalQuantity, `${path}.totalQuantity`),
    totalWithTax: int(order.totalWithTax, `${path}.totalWithTax`),
    currencyCode: str(order.currencyCode, `${path}.currencyCode`),
  };
}

function decodeOrderConnection(value: unknown, path: string): AccountOrderPageResult {
  const orders = record(value, path);
  if (!Array.isArray(orders.items)) throw new Error(`${path}.items must be an array`);
  return {
    totalItems: int(orders.totalItems, `${path}.totalItems`),
    items: orders.items.map((order, index) => decodeOrderSummary(order, `${path}.items[${index}]`)),
  };
}

function decodeCustomer(value: unknown): AccountCustomer | null {
  if (value == null) return null;
  const customer = record(value, "data.activeCustomer");
  const customFields = customer.customFields == null ? {} : record(customer.customFields, "data.activeCustomer.customFields");
  const orders = decodeOrderConnection(customer.orders, "data.activeCustomer.orders");
  const rawType = customFields.customerType;
  const customerType: CustomerKind = rawType === "business" ? "business" : "private";
  return {
    id: str(customer.id, "data.activeCustomer.id"),
    firstName: str(customer.firstName, "data.activeCustomer.firstName"),
    lastName: str(customer.lastName, "data.activeCustomer.lastName"),
    emailAddress: str(customer.emailAddress, "data.activeCustomer.emailAddress"),
    phoneNumber: nullableStr(customer.phoneNumber, "data.activeCustomer.phoneNumber"),
    customerType,
    businessName: nullableStr(customFields.businessName, "data.activeCustomer.customFields.businessName"),
    businessId: nullableStr(customFields.businessId, "data.activeCustomer.customFields.businessId"),
    orderCount: orders.totalItems,
    orders: orders.items,
  };
}

function decodeErrorResult(value: unknown, path: string): Record<string, unknown> {
  const result = record(value, path);
  if (result.__typename === "CurrentUser" || result.__typename === "Success") return result;
  if (typeof result.errorCode === "string" && typeof result.message === "string") {
    throw new AccountDomainError(result.errorCode, result.message);
  }
  throw new Error(`${path} returned an unexpected result`);
}

function decodeBillingSnapshot(value: unknown): AccountBillingSnapshot | null {
  if (value == null) return null;
  const fields = record(value, "data.order.customFields");
  const capturedAt = nullableStr(fields.billingSnapshotAt, "data.order.customFields.billingSnapshotAt");
  const billingName = nullableStr(fields.billingSnapshotName, "data.order.customFields.billingSnapshotName");
  const billingEmail = nullableStr(fields.billingSnapshotEmail, "data.order.customFields.billingSnapshotEmail");
  if (!capturedAt && !billingName && !billingEmail) return null;
  return {
    customerType: fields.billingSnapshotCustomerType === "business" ? "business" : "private",
    businessName: nullableStr(fields.billingSnapshotBusinessName, "data.order.customFields.billingSnapshotBusinessName"),
    businessId: nullableStr(fields.billingSnapshotBusinessId, "data.order.customFields.billingSnapshotBusinessId"),
    billingName,
    billingEmail,
    company: nullableStr(fields.billingSnapshotCompany, "data.order.customFields.billingSnapshotCompany"),
    streetLine1: nullableStr(fields.billingSnapshotStreetLine1, "data.order.customFields.billingSnapshotStreetLine1"),
    streetLine2: nullableStr(fields.billingSnapshotStreetLine2, "data.order.customFields.billingSnapshotStreetLine2"),
    city: nullableStr(fields.billingSnapshotCity, "data.order.customFields.billingSnapshotCity"),
    province: nullableStr(fields.billingSnapshotProvince, "data.order.customFields.billingSnapshotProvince"),
    postalCode: nullableStr(fields.billingSnapshotPostalCode, "data.order.customFields.billingSnapshotPostalCode"),
    countryCode: nullableStr(fields.billingSnapshotCountryCode, "data.order.customFields.billingSnapshotCountryCode"),
    phoneNumber: nullableStr(fields.billingSnapshotPhoneNumber, "data.order.customFields.billingSnapshotPhoneNumber"),
    capturedAt,
  };
}

function decodeProductionStatuses(value: unknown): Map<string, AccountOrderLineStatus> {
  if (!Array.isArray(value)) throw new Error("data.myHappyPrintOrderStatus must be an array");
  const statuses = new Map<string, AccountOrderLineStatus>();
  value.forEach((entry, index) => {
    const status = record(entry, `data.myHappyPrintOrderStatus[${index}]`);
    statuses.set(str(status.orderLineId, `data.myHappyPrintOrderStatus[${index}].orderLineId`), {
      statusGroup: str(status.statusGroup, `data.myHappyPrintOrderStatus[${index}].statusGroup`),
      statusLabelHe: str(status.statusLabelHe, `data.myHappyPrintOrderStatus[${index}].statusLabelHe`),
    });
  });
  return statuses;
}

function decodeOrderArtworkFiles(value: unknown): Map<string, AccountOrderArtworkFile> {
  if (!Array.isArray(value)) throw new Error("data.myHappyPrintArtworkFiles must be an array");
  const files = new Map<string, AccountOrderArtworkFile>();
  value.forEach((entry, index) => {
    const path = `data.myHappyPrintArtworkFiles[${index}]`;
    const item = record(entry, path);
    const orderLineId = str(item.orderLineId, `${path}.orderLineId`);
    // The server returns active files newest-first. Keep the first one if a
    // legacy database ever contains more than one active row for a line.
    if (!files.has(orderLineId)) {
      files.set(orderLineId, {
        originalFilename: str(item.originalFilename, `${path}.originalFilename`),
        version: int(item.version, `${path}.version`),
        downloadUrl: str(item.downloadUrl, `${path}.downloadUrl`),
      });
    }
  });
  return files;
}

function decodeProofStatuses(value: unknown): Map<string, AccountProofStatus> {
  if (!Array.isArray(value)) throw new Error("data.myHappyPrintProofStatuses must be an array");
  const statuses = new Map<string, AccountProofStatus>();
  value.forEach((entry, index) => {
    const path = `data.myHappyPrintProofStatuses[${index}]`;
    const item = record(entry, path);
    const status = str(item.status, `${path}.status`);
    if (!["draft", "sent", "changes_requested", "approved"].includes(status)) {
      throw new Error(`${path}.status is invalid`);
    }
    statuses.set(str(item.orderLineId, `${path}.orderLineId`), {
      version: int(item.version, `${path}.version`),
      status: status as AccountProofStatus["status"],
      staffNote: nullableStr(item.staffNote, `${path}.staffNote`),
      customerComment: nullableStr(item.customerComment, `${path}.customerComment`),
      sentAt: nullableStr(item.sentAt, `${path}.sentAt`),
      respondedAt: nullableStr(item.respondedAt, `${path}.respondedAt`),
      approvedAt: nullableStr(item.approvedAt, `${path}.approvedAt`),
    });
  });
  return statuses;
}

function decodeOrderLineDetails(value: unknown): Map<string, AccountOrderLineDetails> {
  if (!Array.isArray(value)) throw new Error("data.myHappyPrintOrderLineDetails must be an array");
  const details = new Map<string, AccountOrderLineDetails>();
  value.forEach((entry, index) => {
    const path = `data.myHappyPrintOrderLineDetails[${index}]`;
    const item = record(entry, path);
    const kindRaw = str(item.kind, `${path}.kind`);
    if (kindRaw !== "PVC" && kindRaw !== "CATALOG" && kindRaw !== "OTHER") {
      throw new Error(`${path}.kind must be PVC, CATALOG or OTHER`);
    }
    if (!Array.isArray(item.addonCodes) || !item.addonCodes.every(code => typeof code === "string")) {
      throw new Error(`${path}.addonCodes must be a string array`);
    }
    details.set(str(item.orderLineId, `${path}.orderLineId`), {
      kind: kindRaw,
      productSlug: nullableStr(item.productSlug, `${path}.productSlug`),
      widthCm: nullableNumber(item.widthCm, `${path}.widthCm`),
      heightCm: nullableNumber(item.heightCm, `${path}.heightCm`),
      thickness: nullableStr(item.thickness, `${path}.thickness`),
      printSide: nullableStr(item.printSide, `${path}.printSide`),
      shape: nullableStr(item.shape, `${path}.shape`),
      finishing: nullableStr(item.finishing, `${path}.finishing`),
      designService: nullableStr(item.designService, `${path}.designService`),
      rush: nullableBool(item.rush, `${path}.rush`),
      addonCodes: [...item.addonCodes] as string[],
      notes: nullableStr(item.notes, `${path}.notes`),
      requestedDate: nullableStr(item.requestedDate, `${path}.requestedDate`),
      surchargeWithTax: int(item.surchargeWithTax, `${path}.surchargeWithTax`),
      totalWithTax: int(item.totalWithTax, `${path}.totalWithTax`),
    });
  });
  return details;
}

function decodeOrderAddress(value: unknown): AccountOrderAddress | null {
  if (value == null) return null;
  const address = record(value, "data.order.shippingAddress");
  return {
    fullName: nullableStr(address.fullName, "data.order.shippingAddress.fullName"),
    company: nullableStr(address.company, "data.order.shippingAddress.company"),
    streetLine1: nullableStr(address.streetLine1, "data.order.shippingAddress.streetLine1"),
    streetLine2: nullableStr(address.streetLine2, "data.order.shippingAddress.streetLine2"),
    city: nullableStr(address.city, "data.order.shippingAddress.city"),
    province: nullableStr(address.province, "data.order.shippingAddress.province"),
    postalCode: nullableStr(address.postalCode, "data.order.shippingAddress.postalCode"),
    countryCode: nullableStr(address.countryCode, "data.order.shippingAddress.countryCode"),
    phoneNumber: nullableStr(address.phoneNumber, "data.order.shippingAddress.phoneNumber"),
  };
}

function decodeShippingMethods(value: unknown): AccountShippingMethod[] {
  if (!Array.isArray(value)) throw new Error("data.order.shippingLines must be an array");
  return value.map((entry, index) => {
    const line = record(entry, `data.order.shippingLines[${index}]`);
    const method = record(line.shippingMethod, `data.order.shippingLines[${index}].shippingMethod`);
    return {
      code: str(method.code, `data.order.shippingLines[${index}].shippingMethod.code`),
      name: str(method.name, `data.order.shippingLines[${index}].shippingMethod.name`),
      priceWithTax: int(line.priceWithTax, `data.order.shippingLines[${index}].priceWithTax`),
    };
  });
}

function decodeOrder(
  value: unknown,
  statusValue: unknown,
  detailsValue: unknown,
  proofValue: unknown,
  artworkValue: unknown,
): AccountOrderDetails | null {
  if (value == null) return null;
  const order = record(value, "data.order");
  const base = decodeOrderSummary(order, "data.order");
  const statuses = decodeProductionStatuses(statusValue);
  const details = decodeOrderLineDetails(detailsValue);
  const proofStatuses = decodeProofStatuses(proofValue);
  const artworkFiles = decodeOrderArtworkFiles(artworkValue);
  if (!Array.isArray(order.lines)) throw new Error("data.order.lines must be an array");
  return {
    ...base,
    billingSnapshot: decodeBillingSnapshot(order.customFields),
    shippingAddress: decodeOrderAddress(order.shippingAddress),
    shippingWithTax: int(order.shippingWithTax, "data.order.shippingWithTax"),
    shippingMethods: decodeShippingMethods(order.shippingLines),
    lines: order.lines.map((entry, index) => {
      const line = record(entry, `data.order.lines[${index}]`);
      const variant = record(line.productVariant, `data.order.lines[${index}].productVariant`);
      const id = str(line.id, `data.order.lines[${index}].id`);
      return {
        id,
        quantity: int(line.quantity, `data.order.lines[${index}].quantity`),
        totalWithTax: int(line.totalWithTax, `data.order.lines[${index}].totalWithTax`),
        productVariantId: str(variant.id, `data.order.lines[${index}].productVariant.id`),
        productName: str(variant.name, `data.order.lines[${index}].productVariant.name`),
        productionStatus: statuses.get(id) ?? null,
        details: details.get(id) ?? null,
        proofStatus: proofStatuses.get(id) ?? null,
        artworkFile: artworkFiles.get(id) ?? null,
      };
    }),
  };
}

function decodeRepeatOrder(value: unknown): RepeatOrderResult {
  const result = record(value, "data.repeatHappyPrintOrder");
  if (result.__typename === "HappyPrintRepeatOrderSuccess") {
    return {
      ok: true,
      cartId: str(result.cartId, "data.repeatHappyPrintOrder.cartId"),
      copiedLines: int(result.copiedLines, "data.repeatHappyPrintOrder.copiedLines"),
      totalQuantity: int(result.totalQuantity, "data.repeatHappyPrintOrder.totalQuantity"),
      totalWithTax: int(result.totalWithTax, "data.repeatHappyPrintOrder.totalWithTax"),
      currencyCode: str(result.currencyCode, "data.repeatHappyPrintOrder.currencyCode"),
      artworkMustBeResupplied: bool(result.artworkMustBeResupplied, "data.repeatHappyPrintOrder.artworkMustBeResupplied"),
    };
  }
  if (result.__typename === "HappyPrintRepeatOrderFailure") {
    return {
      ok: false,
      code: str(result.code, "data.repeatHappyPrintOrder.code"),
      message: str(result.message, "data.repeatHappyPrintOrder.message"),
    };
  }
  throw new Error("data.repeatHappyPrintOrder returned an unexpected result");
}

export function createAccountApi(client: ShopApiGraphqlClient) {
  return {
    async loadCustomer(signal?: AbortSignal): Promise<AccountCustomer | null> {
      const raw = await client.execute({ operationName: "HappyPrintAccountCustomer", document: ACTIVE_CUSTOMER_QUERY, signal });
      const data = record(raw, "data");
      return decodeCustomer(data.activeCustomer);
    },

    async loadOrders(skip: number, take = 20, signal?: AbortSignal): Promise<AccountOrderPageResult> {
      const raw = await client.execute({
        operationName: "HappyPrintAccountOrders",
        document: ACTIVE_ORDERS_QUERY,
        variables: { skip: Math.max(0, Math.trunc(skip)), take: Math.min(50, Math.max(1, Math.trunc(take))) },
        signal,
      });
      const data = record(raw, "data");
      const customer = record(data.activeCustomer, "data.activeCustomer");
      return decodeOrderConnection(customer.orders, "data.activeCustomer.orders");
    },

    async login(emailAddress: string, password: string, rememberMe = true): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintAccountLogin",
        document: LOGIN_MUTATION,
        variables: { emailAddress, password, rememberMe },
      });
      const data = record(raw, "data");
      decodeErrorResult(data.login, "data.login");
    },

    async logout(): Promise<void> {
      await client.execute({ operationName: "HappyPrintAccountLogout", document: LOGOUT_MUTATION });
    },

    async register(input: RegisterAccountInput): Promise<void> {
      const normalizedPhone = normalizeIsraeliPhoneForRegistration(input.phoneNumber);
      if (!normalizedPhone) {
        throw new AccountDomainError("INVALID_ISRAELI_PHONE", "יש להזין מספר טלפון ישראלי תקין.");
      }

      const business = input.customerType === "business";
      const normalizedBusinessId = business
        ? normalizeIsraeliBusinessIdForRegistration(input.businessId ?? "")
        : null;
      if (business && !normalizedBusinessId) {
        throw new AccountDomainError("INVALID_ISRAELI_BUSINESS_ID", "יש להזין מספר עוסק / ח.פ. / ת״ז תקין.");
      }

      const raw = await client.execute({
        operationName: "HappyPrintRegisterAccount",
        document: REGISTER_MUTATION,
        variables: {
          input: {
            emailAddress: input.emailAddress,
            firstName: input.firstName,
            lastName: input.lastName,
            phoneNumber: normalizedPhone,
            password: input.password,
            customFields: {
              customerType: input.customerType,
              businessName: business ? input.businessName?.trim() || null : null,
              businessId: business ? normalizedBusinessId : null,
            },
          },
        },
      });
      const data = record(raw, "data");
      decodeErrorResult(data.registerCustomerAccount, "data.registerCustomerAccount");
    },

    async verify(token: string): Promise<void> {
      const raw = await client.execute({
        operationName: "HappyPrintVerifyAccount",
        document: VERIFY_MUTATION,
        variables: { token },
      });
      const data = record(raw, "data");
      decodeErrorResult(data.verifyCustomerAccount, "data.verifyCustomerAccount");
    },

    async loadOrder(id: string, signal?: AbortSignal): Promise<AccountOrderDetails | null> {
      const raw = await client.execute({
        operationName: "HappyPrintAccountOrder",
        document: ORDER_QUERY,
        variables: { id },
        signal,
      });
      const data = record(raw, "data");
      return decodeOrder(
        data.order,
        data.myHappyPrintOrderStatus,
        data.myHappyPrintOrderLineDetails,
        data.myHappyPrintProofStatuses,
        data.myHappyPrintArtworkFiles,
      );
    },

    async repeatOrder(id: string): Promise<RepeatOrderResult> {
      const raw = await client.execute({
        operationName: "HappyPrintRepeatOrder",
        document: REPEAT_ORDER_MUTATION,
        variables: { orderId: id },
      });
      const data = record(raw, "data");
      return decodeRepeatOrder(data.repeatHappyPrintOrder);
    },
  };
}

export function createBrowserAccountApi() {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  return createAccountApi(createShopApiGraphqlClient({ endpoint }));
}
